import { DropdownMenuItem } from "@radix-ui/react-dropdown-menu";
import { DropdownMenu as UiDropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "../ui/dropdownMenu";
import Button from "../Button";
import { ChevronDown } from "lucide-react";
import { twMerge } from "tailwind-merge";
import React from "react";
import useUserPermissions from "@/common/hooks/useUserPermissions";

interface DropdownMenuProps {
  triggerLabel: string;
  TriggerIcon?: JSX.Element;
  triggerClassName?: string;
  contentClassName?: string;
  itemClassName?: string;
  dataTestId?: string;
  options: {
    label: string | JSX.Element;
    onClick: (event: React.MouseEvent) => void;
    className?: string;
    disabled?: boolean;
    operationKlassToCheckPermission?: string;
    tooltipMessage?: string;
    dataTestId?: string;
  }[];
  disabled?: boolean;
  operationKlassToCheckPermission?: string;
  menuPosition?: "top" | "right" | "bottom" | "left";
  menuPositionAlign?: "start" | "center" | "end";
  isOpen?: boolean;
  setIsOpen?: (value: boolean) => void;
  tooltipMessage?: string;
}

const DropdownMenu = ({
  triggerLabel,
  TriggerIcon,
  triggerClassName,
  contentClassName,
  itemClassName,
  dataTestId,
  options,
  disabled,
  operationKlassToCheckPermission,
  menuPosition,
  menuPositionAlign,
  isOpen,
  setIsOpen,
  tooltipMessage,
}: DropdownMenuProps) => {
  const uniqueId = React.useId()

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions();

  const isPermissionNeeded = !!operationKlassToCheckPermission;
  const hasPermission = isPermissionNeeded && !isUserPermissionsPending && hasUserPermission(operationKlassToCheckPermission);

  return (
    <UiDropdownMenu
      open={isOpen}
      onOpenChange={setIsOpen}
    >
      <DropdownMenuTrigger
        asChild
        disabled={disabled || (isPermissionNeeded && !hasPermission)}
      >
        <Button
          dataTestId={dataTestId || "dropdown_menu_trigger"}
          className={triggerClassName}
          disabled={disabled || (isPermissionNeeded && !hasPermission)}
          tooltipMessage={tooltipMessage || (isPermissionNeeded && !hasPermission ? 'Você não tem permissão' : undefined)}
        >
          {triggerLabel}

          {TriggerIcon || <ChevronDown />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className={twMerge('bg-black-900 border-none rounded-sm shadow-[0px_0px_8px_4px_rgba(0,_0,_0,_0.2)]', contentClassName)}
        align={menuPositionAlign}
        side={menuPosition}
      >
        {options.map((option, index) => (
          <DropdownMenuItem
            key={`dropdown-menu-item-${uniqueId}-${index}`}
            className={twMerge(
              "hover:bg-white-100 hover:outline-none cursor-pointer",
              itemClassName,
              option.disabled && 'cursor-default text-white-300 hover:bg-transparent',
              option.className
            )}
            disabled={option.disabled}
          >
            <Button
              variant="link-white"
              className="text-white-800 font-normal text-sm px-3 py-2 w-full justify-start"
              operationKlassToCheckPermission={option.operationKlassToCheckPermission}
              disabled={option.disabled}
              tooltipMessage={option.tooltipMessage}
              onClick={(event) => !option.disabled && option.onClick(event)}
              dataTestId={option.dataTestId}
            >
              {option.label}
            </Button>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </UiDropdownMenu>
  );
};

export default DropdownMenu;
